import { Skeleton } from '@chakra-ui/react';
import Board from '@/components/Board';
import CandidateBoardCard from '@/components/CandidateBoardCard';
import { CandidateBoardCardSkeleton } from '@/components/CandidateBoardCard/CandidateBoardCard.component';
import ListHeader from '@/components/ListHeader';
import useCandidateStatuses from '@/hooks/useCandidateStatuses';
import { getEndpointWithParams } from '@/routes/config';
import { Paths } from '@/types/routes';
import { DEFAULT_STATUS_COLOR, getFormattedStatus } from '@/utils/format';
import * as Styles from './CandidatesBoard.styles';

const CandidatesBoard = ({ data, isLoading, isAllCandidates }: any): JSX.Element => {
  const candidateStatuses = useCandidateStatuses();

  const renderSkeletonBoard = (): JSX.Element => (
    <>
      <Board.Column>
        <div className="tw-flex tw-w-full tw-justify-start tw-items-center tw-h-6 tw-gap-4 tw-mb-2.5">
          <Skeleton className="tw-h-4 tw-w-[64px]" />
          <Skeleton className="tw-h-3.5 tw-w-4" />
        </div>
        <Board.Cards>
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
        </Board.Cards>
      </Board.Column>
      <Board.Column>
        <div className="tw-flex tw-w-full tw-justify-start tw-items-center tw-h-6 tw-gap-4 tw-mb-2.5">
          <Skeleton className="tw-h-4 tw-w-[64px]" />
          <Skeleton className="tw-h-3.5 tw-w-4" />
        </div>
        <Board.Cards>
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
        </Board.Cards>
      </Board.Column>
      <Board.Column>
        <div className="tw-flex tw-w-full tw-justify-start tw-items-center tw-h-6 tw-gap-4 tw-mb-2.5">
          <Skeleton className="tw-h-4 tw-w-[64px]" />
          <Skeleton className="tw-h-3.5 tw-w-4" />
        </div>
        <Board.Cards>
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
        </Board.Cards>
      </Board.Column>
      <Board.Column>
        <div className="tw-flex tw-w-full tw-justify-start tw-items-center tw-h-6 tw-gap-4 tw-mb-2.5">
          <Skeleton className="tw-h-4 tw-w-[64px]" />
          <Skeleton className="tw-h-3.5 tw-w-4" />
        </div>
        <Board.Cards>
          <CandidateBoardCardSkeleton />
          <CandidateBoardCardSkeleton />
        </Board.Cards>
      </Board.Column>
    </>
  );

  const title = !isAllCandidates ? 'Talent Pool' : undefined;
  return (
    <>
      <ListHeader showActionsArea={false} title={title} className="tw-h-[44px]" />
      <Styles.BoardContainer>
        <Board>
          {isLoading
            ? renderSkeletonBoard()
            : Object.entries(data || {}).map(([key, value]: any) => {
                const formattedStatus = getFormattedStatus(key);
                const compareLabelAndStatus = (status): boolean => {
                  return getFormattedStatus(status?.label) === formattedStatus;
                };
                const filteredStage = candidateStatuses.find(compareLabelAndStatus);
                const stageColor = filteredStage?.color ?? DEFAULT_STATUS_COLOR;

                const candidates = isAllCandidates
                  ? value
                  : value.filter((candidate) => !!candidate.isTalentPool);

                return (
                  <Board.Column key={key}>
                    <Board.ColumnTitle
                      title={formattedStatus}
                      titleColor={stageColor}
                      count={candidates.length}
                    />
                    <Board.Cards>
                      {(candidates ?? []).map((talent: any) => {
                        const candidateUrl = isAllCandidates
                          ? Paths.CANDIDATE_DETAILS
                          : Paths.TALENT_POOL_DETAILS;

                        const candidateLink = getEndpointWithParams(candidateUrl, {
                          id: talent.id,
                        });

                        return (
                          <CandidateBoardCard
                            key={`${talent.id}`}
                            candidateLink={candidateLink}
                            firstName={talent.firstName}
                            lastName={talent.lastName}
                            status={talent.status}
                            inStatusSince={talent.inStatusSince}
                            owner={talent.candidateOwner}
                            stack={talent.mainTechStack}
                          />
                        );
                      })}
                    </Board.Cards>
                  </Board.Column>
                );
              })}
        </Board>
      </Styles.BoardContainer>
    </>
  );
};

export default CandidatesBoard;
